import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useTranslation } from "react-i18next";
import styles from './AutoCompleteWithTags.module.css';

const AutoCompleteWithTags = ({
  suggestions = [],
  placeholder = "",
  label = "",
  onSelectionChange = () => {},
  selectedValues = []
}) => {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const filtered = suggestions.filter(suggestion =>
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selectedValues.some(selected => selected.toLowerCase() === suggestion.toLowerCase())
    );
    setFilteredSuggestions(filtered);
  }, [inputValue, suggestions, selectedValues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addValue(inputValue.trim());
    } else if (e.key === 'Backspace' && !inputValue && selectedValues.length > 0) {
      removeValue(selectedValues[selectedValues.length - 1]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const addValue = (value) => {
    if (value && !selectedValues.includes(value)) {
        onSelectionChange([...selectedValues, value]);
        setInputValue('');
        setIsOpen(false);
    }
  };

  const removeValue = (valueToRemove) => {
    const newValues = selectedValues.filter(value => value !== valueToRemove);
    onSelectionChange(newValues);
  };

  const handleSuggestionClick = (suggestion) => {
    addValue(suggestion);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <label className={styles.label}>
        {label}
        <hr className={styles.input__hr} />
      </label>

      <div className={styles.inputContainer}>
        <div className={`${styles.inputWrapper} ${isOpen ? styles.inputWrapperFocused : ''}`}>
          <div className={styles.tagsContainer}>
            {selectedValues.map((value, index) => (
              <span key={index} className={styles.tag}>
                {value}
                <button
                  type="button"
                  onClick={() => removeValue(value)}
                  className={styles.tagButton}
                >
                  <X size={12} />
                </button>
              </span>
            ))}

            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              className={styles.input}
            />

            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={styles.dropdownButton}
            >
              <ChevronDown
                size={16}
                className={`${styles.chevronIcon} ${isOpen ? styles.chevronRotated : ''}`}
              />
            </button>
          </div>
        </div>

        {isOpen && (
          <div className={styles.dropdown}>
            {inputValue && !filteredSuggestions.some(s => s.toLowerCase() === inputValue.toLowerCase()) && (
              <div
                onClick={() => addValue(inputValue)}
                className={`${styles.dropdownItem} ${styles.addNewItem}`}
              >
                <span className={styles.addNewLabel}>{t('AutocompleteWithTags.addNew')}: </span>
                <span className={styles.addNewValue}>"{inputValue}"</span>
              </div>
            )}

            {filteredSuggestions.length > 0 ? (
              filteredSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={styles.dropdownItem}
                >
                  {suggestion}
                </div>
              ))
            ) : (
              !inputValue && (
                <div className={styles.dropdownItem}>
                  <span className={styles.noOptionsText}>{t('AutocompleteWithTags.noOptions')}</span>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {selectedValues.length > 0 && (
        <div className={styles.counter}>
          {t('AutocompleteWithTags.option', { count: selectedValues.length })}
        </div>
      )}
    </div>
  );
};

export default AutoCompleteWithTags;